import React, { useState, useEffect, useMemo } from 'react';
import { UIUser } from 'types';
import OfflineDriveDropdown from '../offlineDriveCandidatesList/OfflineDriveDropdown';
import { useUserContext } from 'src/context/UserProvider';
import { Modal, Button, Label } from 'flowbite-react';
import { useSearchParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { useGetEligibleDrives } from 'src/hooks/offlineDrive.hooks';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';

interface Props {
    user?: UIUser;
    remarks: string;
    setRemarks: React.Dispatch<React.SetStateAction<string>>;
    openRegisterForDriveModal: boolean;
    setOpenRegisterForDriveModal: React.Dispatch<React.SetStateAction<boolean>>;
    handleRegisterForDrive: ({ user, driveId, remarks, driveLabel }: { user: string; driveId: string; remarks: string; driveLabel?: string }) => void;
}

const RegisterForDriveModal = ({
    user,
    openRegisterForDriveModal,
    setOpenRegisterForDriveModal,
    handleRegisterForDrive,
    remarks,
    setRemarks
}: Props) => {

    const {
        selectedDrive,
        setSelectedDrive,
    } = useUserContext();
    const [searchParams] = useSearchParams();
    const driveId = searchParams.get('driveId') || selectedDrive?.value;
    const [selectedDriveLabel, setSelectedDriveLabel] = useState<{ label: string; value: string } | null>(null);

    // Fetch eligible drives to get drive details
    const { data: eligibleDrivesData } = useGetEligibleDrives({ driveId: driveId || undefined });

    // Fetch placement drive labels from dropdown menu
    const { data: placementDriveData } = useGetDropdownItemsByCategory('PLACEMENT_DRIVE', undefined, true);

    // Get the selected drive details
    const selectedDriveDetails = useMemo(() => {
        if (!driveId) return null;
        if (eligibleDrivesData?.eligibleDrives) {
            return eligibleDrivesData.eligibleDrives.find(drive => drive._id === driveId);
        }
        return null;
    }, [driveId, eligibleDrivesData]);

    // Get drive label options based on selected drive's driveLabel field
    const driveLabelOptions = useMemo(() => {
        if (!selectedDriveDetails?.driveLabel) return [];

        const driveLabels: string[] = Array.isArray(selectedDriveDetails.driveLabel)
            ? selectedDriveDetails.driveLabel
            : [String(selectedDriveDetails.driveLabel)];

        if (driveLabels.length === 0) return [];

        // If placement drive data is available, try to match with dropdown menu items
        if (placementDriveData?.lmsTechStacks && placementDriveData.lmsTechStacks.length > 0) {
            const placementDriveMap = new Map<string, string>();
            placementDriveData.lmsTechStacks
                .filter(item => item.isActive)
                .forEach(item => {
                    const itemLabel = item.label || item.name;
                    placementDriveMap.set(itemLabel, itemLabel);
                    if (item.name && item.name !== itemLabel) {
                        placementDriveMap.set(item.name, itemLabel);
                    }
                });

            return driveLabels
                .map(driveLabel => {
                    const matchedLabel = placementDriveMap.get(driveLabel) || driveLabel;
                    return {
                        label: matchedLabel,
                        value: matchedLabel,
                    };
                })
                .filter((option, index, self) => {
                    const firstIndex = self.findIndex(o => o.label === option.label);
                    return index === firstIndex;
                });
        }

        // Fallback: show labels directly
        return driveLabels.map(label => ({
            label: label,
            value: label,
        }));
    }, [selectedDriveDetails, placementDriveData]);

    // Reset selected drive label when drive changes
    useEffect(() => {
        setSelectedDriveLabel(null);
    }, [driveId]);
    return (
        <Modal show={openRegisterForDriveModal} onClose={() => setOpenRegisterForDriveModal(false)}>
            <Modal.Header className='font-semibold text-[12px] '>
                Register in Drive
                <h3
                    className='text-red-500 text-[17px] leading-6 mt-2'
                >
                    Please use this feature of manually adding user to drive only if its a exception case where otp is not coming or user is not able to register via scanner after multiple attempts.
                </h3>
            </Modal.Header>
            <Modal.Body>
                <div className="flex justify-between mb-4">
                    <div>
                        <p className="text-sm text-gray-600">
                            <strong>Name:</strong> {user?.name}
                        </p>
                        <p className="text-sm text-gray-600">
                            <strong>Email:</strong> {user?.email}
                        </p>
                        <p className="text-sm text-gray-600">
                            <strong>Student Code:</strong> {user?.studentCode}
                        </p>
                        <div className="mt-4">
                            <label htmlFor="remarks" className="block text-sm font-medium text-gray-700">
                                Remarks
                            </label>
                            <input
                                type="text"
                                name="remarks"
                                id="remarks"
                                className="mt-1 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                onChange={(e) => setRemarks(e.target.value)}
                                value={remarks}
                            />
                        </div>
                        {driveId && driveLabelOptions.length > 0 && (
                            <div className="mt-4">
                                <Label htmlFor="driveLabel" value="Drive Label (Placement Drive)" />
                                <Select
                                    menuPlacement={'auto'}
                                    classNamePrefix="my-select-plans"
                                    closeMenuOnSelect={true}
                                    placeholder="Select drive label"
                                    options={driveLabelOptions}
                                    value={selectedDriveLabel}
                                    onChange={(value: SingleValue<{ label: string; value: string }>) => {
                                        setSelectedDriveLabel(value);
                                    }}
                                    isClearable
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Select the drive label for this registration. This can be edited later.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className='flex justify-between'>
                <div className="md:min-w-[150px]">
                    <OfflineDriveDropdown selectedDrive={selectedDrive} setSelectedDrive={setSelectedDrive} />
                </div>
                <div className='flex flex-row gap-4'>
                    <Button color="gray" onClick={() => setOpenRegisterForDriveModal(false)}>
                        Cancel
                    </Button>
                    {driveId ? (
                        <Button
                            onClick={() => {
                                if (user?._id) {
                                    handleRegisterForDrive({
                                        user: user._id,
                                        driveId: driveId,
                                        remarks: remarks,
                                        driveLabel: selectedDriveLabel?.value || undefined
                                    });
                                }
                                setOpenRegisterForDriveModal(false);
                            }}
                        >
                            {selectedDriveLabel ? 'Update Registration' : 'Register Now'}
                        </Button>
                    ) : (
                        <p className="text-red-500">Select a drive to register</p>
                    )}
                </div>

            </Modal.Footer>
        </Modal>
    );
};

export default RegisterForDriveModal;
